import { API_HEAD } from '@/lib/apiConfig';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface Admin {
  fullName: string;
  email: string;
  role: string;
  avatar?: string;
}

export interface LoginResponse {
  success: boolean;
  token: string;
  admin: Admin;
  message?: string;
}

class AuthService {
  private getAuthToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('adminToken');
  }

  private setAuthToken(token: string): void {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem('adminToken', token);
      // Đảm bảo token đã được lưu bằng cách đọc lại
      const saved = localStorage.getItem('adminToken');
      if (saved !== token) {
        console.warn('Token không được lưu đúng cách, thử lại...');
        localStorage.setItem('adminToken', token);
      }
    } catch (error) {
      console.error('Lỗi khi lưu token:', error);
    }
  }

  private removeAuthToken(): void {
    if (typeof window === 'undefined') return;
    localStorage.removeItem('adminToken');
  }

  private getHeaders(): HeadersInit {
    const token = this.getAuthToken();
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  // Helper method để retry request với token refresh nếu cần
  async requestWithRefresh(url: string, options: RequestInit = {}): Promise<Response> {
    const token = this.getAuthToken();
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...options.headers,
    };

    let response = await fetch(url, {
      ...options,
      headers,
    });

    // Nếu 401, thử refresh token và retry
    if (response.status === 401 && token) {
      const refreshed = await this.refreshToken();
      if (refreshed) {
        const newToken = this.getAuthToken();
        const newHeaders = {
          'Content-Type': 'application/json',
          ...(newToken && { 'Authorization': `Bearer ${newToken}` }),
          ...options.headers,
        };
        response = await fetch(url, {
          ...options,
          headers: newHeaders,
        });
      }
    }

    return response;
  }

  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    // Xóa token cũ trước khi đăng nhập mới để tránh conflict
    this.removeAuthToken();
    
    const response = await fetch(`${API_HEAD}admin/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(credentials),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Đăng nhập thất bại');
    }

    if (data.success && data.token) {
      this.setAuthToken(data.token);
    }

    return data;
  }

  async logout(): Promise<void> {
    this.removeAuthToken();
  }

  async refreshToken(): Promise<boolean> {
    const token = this.getAuthToken();
    if (!token) return false;

    try {
      const response = await fetch(`${API_HEAD}admin/refresh-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      if (data.success && data.token) {
        this.setAuthToken(data.token);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error refreshing token:', error);
      return false;
    }
  }

  async getCurrentAdmin(): Promise<Admin | null> {
    const token = this.getAuthToken();
    if (!token) return null;

    try {
      // Đảm bảo token được đọc đúng cách
      const currentToken = this.getAuthToken();
      if (!currentToken || currentToken !== token) {
        console.warn('Token không khớp, có thể do race condition');
        return null;
      }

      // Tạo AbortController để timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 giây timeout
      
      const response = await fetch(`${API_HEAD}admin/profile`, {
        method: 'GET',
        headers: this.getHeaders(),
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);

      if (!response.ok) {
        // Nếu token không hợp lệ hoặc hết hạn, thử refresh trước
        if (response.status === 401 || response.status === 403) {
          const refreshed = await this.refreshToken();
          if (refreshed) {
            // Đợi một chút để đảm bảo token đã được lưu
            await new Promise(resolve => setTimeout(resolve, 100));
            // Thử lại request sau khi refresh
            const retryController = new AbortController();
            const retryTimeoutId = setTimeout(() => retryController.abort(), 10000);
            
            const retryResponse = await fetch(`${API_HEAD}admin/profile`, {
              method: 'GET',
              headers: this.getHeaders(),
              signal: retryController.signal,
            });
            
            clearTimeout(retryTimeoutId);
            if (retryResponse.ok) {
              const retryData = await retryResponse.json();
              return retryData.success ? retryData.admin : null;
            }
          }
          // Nếu refresh thất bại, mới xóa token
          this.removeAuthToken();
        }
        return null;
      }

      const data = await response.json();
      return data.success ? data.admin : null;
    } catch (error) {
      // Bỏ qua AbortError (timeout)
      if (error instanceof Error && error.name === 'AbortError') {
        console.warn('Request timeout khi lấy thông tin admin');
        return null;
      }
      console.error('Error getting current admin:', error);
      // Thử refresh token trước khi xóa
      const refreshed = await this.refreshToken();
      if (!refreshed) {
        this.removeAuthToken();
      }
      return null;
    }
  }

  isAuthenticated(): boolean {
    return !!this.getAuthToken();
  }

  getToken(): string | null {
    return this.getAuthToken();
  }

  async updateProfile(fullName: string): Promise<{ success: boolean; admin?: Admin; message?: string }> {
    const response = await fetch(`${API_HEAD}admin/profile`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify({ fullName }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Cập nhật thông tin thất bại');
    }

    return data;
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<{ success: boolean; message?: string }> {
    const response = await fetch(`${API_HEAD}admin/change-password`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify({ currentPassword, newPassword }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Đổi mật khẩu thất bại');
    }

    return data;
  }
}

export default new AuthService();
